import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, FileText, MapPin, Package, CreditCard, Download } from 'lucide-react';
import axios from 'axios';
import { PortalSidebar } from './PortalSidebar';
import { PortalTabs } from './PortalTabs';
import { OverviewTab } from './OverviewTab';
import { InvoicesTab } from './InvoicesTab';
import { LocationsTab } from './LocationsTab';
import { ContractsTab } from './ContractsTab';
import { SubscriptionsTab } from './SubscriptionsTab';
import { PaymentsTab } from './PaymentsTab';
import { FilesTab } from './FilesTab';
import { DocumentsTab } from './DocumentsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalOverviewPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [clientFiles, setClientFiles] = useState([]);
  const [clientDocuments, setClientDocuments] = useState({ folders: [], documents: [] });
  const [showAddCard, setShowAddCard] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadCards = () => {
    axios.get(`${API_URL}/api/portal/cards`, { headers }).then(res => setCreditCards(res.data || [])).catch(() => {});
  };

  useEffect(() => {
    if (!token) { navigate('/portal/login'); return; }
    Promise.all([
      axios.get(`${API_URL}/api/portal/me`, { headers }),
      axios.get(`${API_URL}/api/portal/invoices`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/portal/contracts`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/portal/subscriptions`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/portal/payments`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/portal/locations`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/portal/cards`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/portal/files`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/portal/documents`, { headers }).catch(() => ({ data: { folders: [], documents: [] } })),
    ]).then(([me, inv, con, sub, pay, loc, cards, files, docs]) => {
      setClient(me.data);
      setInvoices(inv.data || []);
      setContracts(con.data || []);
      setSubscriptions(sub.data || []);
      setPayments(pay.data || []);
      setLocations(loc.data || []);
      setCreditCards(cards.data || []);
      setClientFiles(files.data || []);
      setClientDocuments(docs.data || { folders: [], documents: [] });
    }).catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied!'); };

  const handlePayInvoice = async (id, method) => {
    try {
      const res = await axios.post(`${API_URL}/api/payments/checkout/invoice`, { invoice_id: id, origin_url: window.location.origin, payment_method: method || 'cc' });
      window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Payment failed'); }
  };

  const handleAddCard = async (cardData) => {
    try {
      await axios.post(`${API_URL}/api/portal/cards`, cardData, { headers });
      toast.success('Card added');
      setShowAddCard(false);
      loadCards();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add card'); }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Remove this card?')) return;
    try {
      await axios.delete(`${API_URL}/api/portal/cards/${cardId}`, { headers });
      toast.success('Card removed');
      loadCards();
    } catch (err) { toast.error('Failed to remove card'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#84CC16] border-t-transparent rounded-full animate-spin" /></div>;
  if (!client) return <div className="text-center py-12 text-gray-500">Unable to load profile</div>;

  const fullName = `${client.first_name || ''} ${client.last_name || ''}`.trim();
  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'invoices', label: `Invoices (${invoices.length})`, icon: FileText },
    { id: 'locations', label: `Locations (${locations.length})`, icon: MapPin },
    { id: 'documents', label: `Documents (${clientDocuments.documents.length})`, icon: FileText },
    { id: 'subscriptions', label: `Subscriptions (${subscriptions.length})`, icon: Package },
    { id: 'payments', label: 'Payment History', icon: CreditCard },
  ];

  return (
    <div data-testid="portal-overview">
      <div className="flex gap-6">
        <PortalSidebar client={client} fullName={fullName} />
        <div className="flex-1 space-y-6">
          <PortalTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
          {activeTab === 'overview' && <OverviewTab client={client} fullName={fullName} copy={copy} fmtDate={fmtDate} />}
          {activeTab === 'invoices' && <InvoicesTab invoices={invoices} fmt={fmt} fmtDate={fmtDate} onPay={handlePayInvoice} />}
          {activeTab === 'locations' && <LocationsTab locations={locations} />}
          {activeTab === 'documents' && <DocumentsTab token={token} folders={clientDocuments.folders} documents={clientDocuments.documents} />}
          {activeTab === 'subscriptions' && <SubscriptionsTab subscriptions={subscriptions} fmt={fmt} fmtDate={fmtDate} />}
          {activeTab === 'payments' && <PaymentsTab payments={payments} fmt={fmt} fmtDate={fmtDate} />}
        </div>
      </div>
    </div>
  );
}
